import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Modal } from 'antd';
import { Button } from '@cropler/ui-kit';
import axios from 'axios';
import dayjs from 'dayjs';
import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import debounce from 'lodash.debounce';

import styles from './styles.module.scss';

import { mapBoxId, mapBoxStyles, MapLat, MapLng, SentinelHubId, SentinelHubLayerName } from '../../constants/keys';
import {
  ActiveBorderPolygon,
  BreakpointMobile,
  BreakpointMobileUp,
  DefaultBgPolygon,
  PathnamesMap,
} from '../../constants/ui';
import { DateFormat } from '../../constants/data';

import { useGetSensorsDatesQuery, useGetSensorsSummaryQuery } from '../../store/api/sensors';
import { selectDate, selectField, selectMainSensor, selectSeason, updateSorting } from '../../store/slices/sensors';
import { useGetBoundariesQuery, useGetFieldsQuery } from '../../store/api/fields';

import { EmptyView } from '../EmptyView/index.jsx';
import { CropsSelect } from '../CropsSelect/CropsSelect.jsx';
import {
  MapFilterMetrics,
  MapFilterMetricsParam,
  MapMarker,
  MapMarkerDate,
  MapMarkerField,
  MapMobileSwitchView,
  NdviScale,
  NDVIToggleButton,
} from './index.jsx';
import { getClosestDate } from '../../utils/dates';
import { Loading } from '../index.jsx';
import { useViewport, useSelectedDate, useLocalStorage } from '../../hooks';
import {
  getBboxFromMultipolygon,
  getCentroidCoordinates,
  multiPolygonToEPSG3857,
  multiPolygonToWKT,
  removePolygonSources,
} from '../../utils/fields';
import { PhotosTypeSwitchDark } from '../PhotosTypeSwitch/index.jsx';
import { FilterDate } from '../Filters/index.jsx';
import {
  FilterParameters,
  LayerNDVIName,
  SourceNDVIName,
  TileMinZoom,
  WmsImageName,
  WmsLayerImageName,
} from './constants/index.jsx';
import {
  Sidebar,
  SidebarFilter,
  SidebarItemButton,
  SidebarList,
  SidebarListExpanded,
  SidebarField,
} from '../Sidebar/index.jsx';
import { goToTimeline } from '../../utils/common';
import { GetLocationValues, GetMarkerValue, GetNDVIArrayFromImage } from './libs/index.jsx';
import { getSafeAreaInsetTop } from '../../utils/ui';

mapboxgl.accessToken = mapBoxId;

const Map = ({ t }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const { widthViewport } = useViewport();

  const {
    selectedDate,
    photosType,
    sorting: { dir, parameter, crop },
    selectedSensor,
    // selectedField,
  } = useSelector((state) => state.sensors);
  const { crops } = useSelector((state) => state.catalog);
  const { selectedField } = useSelector((state) => state.fields);

  const [lng, setLng] = useState(21.41524);
  const [lat, setLat] = useState(53.5784);
  const [mapLng, setMapLng] = useLocalStorage(MapLng, '');
  const [mapLat, setMapLat] = useLocalStorage(MapLat, '');

  const [activeAsideMobile, setActiveAsideMobile] = useState(false);
  const [activeParametersMobile, setActiveParametersMobile] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState('');
  const [zoom, setZoom] = useState(TileMinZoom);
  const [filteredFields, setFilteredFields] = useState([]);
  const [unboundSensors, setUnboundSensors] = useState([]);
  const [currentCrops, setCurrentCrops] = useState([]);
  const [styleMapLoaded, setStyleMapLoaded] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [markersFields, setMarkersFields] = useState([]);
  const [markersDate, setMarkersDate] = useState([]);
  const [hubImagesDates, setHubImagesDates] = useState([]); // all dates
  const [polygons, setPolygons] = useState([]);
  const [filterParam, setFilterParam] = useState(FilterParameters[2]);
  const [filterCrop, setFilterCrop] = useState(crop);
  // const [activeSortingPanel, setActiveSortingPanel] = useState(false);
  const [activeNDVI, setActiveNDVI] = useState(false);

  const [listSourceNDVI, setListSourceNDVI] = useState([]);
  const listSourceNDVIRef = useRef(listSourceNDVI);

  const [modalOpen, setModalOpen] = useState(false);
  const [imageFull, setImageFull] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [centeredMarkers, setCenteredMarkers] = useState(false);
  const [centeredField, setCenteredField] = useState('');
  const [centeredLocation, setCenteredLocation] = useState('');

  const {
    data: dates = [],
    isSuccess: isGetSensorsDatesSuccess,
    isFetching: isGetSensorsDatesLoading,
    isError: isGetSensorsDatesError,
    error: getSensorsDatesError,
  } = useGetSensorsDatesQuery({
    crop_id: crop === 'all' ? undefined : crop,
  });
  const { data: fields, isSuccess: isFieldsSuccess } = useGetFieldsQuery({ ordering: `${dir}name` });
  const { data: boundaries, isSuccess: isBoundariesSuccess } = useGetBoundariesQuery();

  useSelectedDate(dates);

  const {
    data: sensors,
    isSuccess: isSensorsSuccess,
    isFetching: isGetSensorsLoading,
  } = useGetSensorsSummaryQuery(
    { date: selectedDate, query: searchParams.get('search') }, //ordering: `${dir}${SortingData[parameter]}`
    { skip: !selectedDate, refetchOnMountOrArgChange: true },
  );

  const roundOffNumber = (value) => {
    return Number(value).toFixed(8);
  };

  const clickImage = ({ image }) => {
    setImageFull(image);
    setModalOpen(true);
  };

  const clickMarker = ({ id, image }) => {
    if (widthViewport > BreakpointMobile) {
      goToTimeline({ id, date: selectedDate, navigate });
    } else {
      setActiveMarkerId(id);
      clickImage({ image: image });
    }
  };

  const flyToLocation = ({ longitude, latitude, locationId = null, seasonId = null, markersList }) => {
    setLng(longitude);
    setLat(latitude);
    setMapLng(longitude);
    setMapLat(latitude);
    map.current.flyTo({
      center: [longitude, latitude],
      speed: 1.5,
      zoom: TileMinZoom,
    });

    setActiveAsideMobile(false);

    if (locationId) {
      dispatch(selectMainSensor({ id: locationId }));
      setMapLng(longitude);
      setMapLat(latitude);
      setCenteredLocation(locationId);

      // Raise z-index of the active marker above the others
      (markersList || markers).forEach((m) => {
        const el = m.getElement();
        if (el) {
          el.style.zIndex = m._locationId === locationId ? '10' : '';
        }
      });
    }

    setCenteredField('');
    dispatch(selectSeason(seasonId));
  };

  const flyToField = ({ locationId, season }) => {
    if (!season) {
      return;
    }
    const boundary = boundaries.filter((obj) => obj.id === season.boundaryId)[0];
    if (boundary) {
      const centroid = getCentroidCoordinates(boundary.geom.coordinates[0][0]);
      if (centroid) {
        flyToLocation({
          longitude: centroid[0],
          latitude: centroid[1],
          locationId,
          seasonId: season.id,
          isField: true,
        });
      }
      setCenteredField(season.boundaryId);
      setCenteredLocation('');
    }
    dispatch(selectField());
  };

  const onChangeFilterParam = ({ target: { value } }) => {
    setFilterParam(value);
    setActiveParametersMobile(false);
  };

  const changeDate = (date) => {
    dispatch(selectDate(date));
    searchParams.set('date', date);
    setSearchParams(searchParams, { replace: true });
  };

  const onChangeFilterCrop = (value) => {
    if (value.id === 'all') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', value.id);
    }
    setSearchParams(searchParams, { replace: true });
    setFilterCrop(value.id);
    dispatch(updateSorting({ dir, parameter, crop: value.id }));
  };

  const removeNDVISources = (id) => {
    const source = map.current.getSource(SourceNDVIName + id);
    if (source) {
      map.current.removeLayer(LayerNDVIName + id);
      map.current.removeSource(SourceNDVIName + id);
    }
    const sourceImage = map.current.getSource(WmsImageName + id);
    if (sourceImage) {
      map.current.removeLayer(WmsLayerImageName + id);
      map.current.removeSource(WmsImageName + id);
    }
  };

  const toggleNDVITTiles = ({ status }) => {
    setActiveNDVI(status);
  };

  const onAddField = () => {
    navigate('/fields');
  };

  // Get all existing NDVI dates from sentinel-hub.com (only once for every sensor)
  const getHubImagesDates = () => {
    let list = [];

    filteredFields.map((field) => {
      if (field.id !== 'unbound') {
        const location = field.locations.length ? field.locations[0] : null;
        if (location) {
          if (!hubImagesDates.length || !hubImagesDates.find((item) => item.locationId === location.id)) {
            const geoJson = {
              coordinates: field.season.coordinates,
              type: 'MultiPolygon',
            };
            const bbox = getBboxFromMultipolygon(geoJson);

            list.push({
              locationId: location.id,
              bbox: bbox, // bbox: { minLng, minLat, maxLng, maxLat },
              coordinates: field.season.coordinates,
              installDate: location.install_date,
              removalDate: location.removal_date,
              dates: [],
            });
          }
        }
      }
    });

    const fetchData = async () => {
      try {
        if (list.length) {
          const results = await Promise.all(
            list.map((item) => {
              const period = `${dayjs(item.installDate).add(-14, 'day').format(DateFormat)}/${dayjs(item.removalDate)
                .add(14, 'day')
                .format(DateFormat)}`;
              return axios.get(`https://services.sentinel-hub.com/ogc/wfs/${SentinelHubId}`, {
                params: {
                  service: 'WFS',
                  version: '2.0.0',
                  request: 'GetFeature',
                  typeNames: 'DSS2',
                  outputFormat: 'application/json',
                  srsName: 'EPSG:4326',
                  bbox: item.bbox.join(','),
                  time: period,
                  maxFeatures: '360',
                },
              });
            }),
          );
          results.map((result, i) => {
            let dates = [];
            result.data.features.map((item) => {
              if (!dates.find((d) => d === item.properties.date)) {
                dates.push(item.properties.date);
              }
            });
            list[i].dates = dates;
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData().then(() => {
      let fullList = [...list];
      if (hubImagesDates.length) {
        hubImagesDates.map((item) => {
          fullList.push(item);
        });
      }
      setHubImagesDates(fullList);

      // createMarkersDatesNDVI() after createSourceNDVI()
      createSourceNDVI(fullList)
        .then(() => {
          createMarkersDatesNDVI(fullList);
        })
        .catch((error) => {
          console.error('Error in createSourceNDVI:', error);
          createMarkersDatesNDVI(fullList);
        });
    });
  };

  // Add NDVI polygons
  const createSourceNDVI = (dates) => {
    let list = [];

    if (listSourceNDVI.length) {
      listSourceNDVI.map((item) => {
        if (!filteredFields.find((obj) => obj.locations.length && obj.locations[0].id === item.id)) {
          removeNDVISources(item.id);
        }
      });
    }

    const ndviPromises = [];

    filteredFields.map((field) => {
      const season = field.season;
      if (field.locations.length && field.id !== 'unbound') {
        const location = field.locations ? field.locations[0] : null;
        const id = field.locations ? field.locations[0].id : null;
        const coordinates = season.coordinates;

        const sensorHub = id ? dates.filter((obj) => obj.locationId === id) : null;
        let queryDate = selectedDate;
        let targetDate = new Date(selectedDate);
        if (sensorHub.length) {
          queryDate = getClosestDate(sensorHub[0].dates, targetDate) || selectedDate;
        }

        const sensorLng = location
          ? location.gps_point
            ? location.gps_point.coordinates[0]
            : location.install_point.coordinates[0]
          : '';
        const sensorLat = location
          ? location.gps_point
            ? location.gps_point.coordinates[1]
            : location.install_point.coordinates[1]
          : '';

        if (
          !listSourceNDVI.length ||
          (listSourceNDVI.length && !listSourceNDVI.find((item) => item.id === id && item.date === queryDate))
        ) {
          removeNDVISources(id);

          let geojson = {
            coordinates: coordinates,
            type: 'MultiPolygon',
          };
          // simplify polygons (error 404 too long query)
          geojson = turf.simplify(geojson, { tolerance: 0.0001, highQuality: true });

          const geoJsonEPSG3857 = multiPolygonToEPSG3857(geojson);
          const bbox = getBboxFromMultipolygon(geojson);
          const wkt = multiPolygonToWKT(geoJsonEPSG3857);
          const imageUrl = `https://services.sentinel-hub.com/ogc/wms/${SentinelHubId}?REQUEST=GetMap&CRS=EPSG:3857&LAYERS=${SentinelHubLayerName}&TIME=${queryDate}&GEOMETRY=${wkt}&RESX=10&RESY=10&FORMAT=image/png`;

          if (map.current && !map.current.getSource(SourceNDVIName + id)) {
            map.current.addSource(SourceNDVIName + id, {
              type: 'geojson',
              data: geojson,
            });

            map.current.addSource(WmsImageName + id, {
              type: 'image',
              url: imageUrl,
              coordinates: [
                [bbox[0], bbox[3]],
                [bbox[2], bbox[3]],
                [bbox[2], bbox[1]],
                [bbox[0], bbox[1]],
              ],
            });

            map.current.addLayer({
              id: WmsLayerImageName + id,
              type: 'raster',
              source: WmsImageName + id,
              paint: {},
            });

            map.current.addLayer({
              id: LayerNDVIName + id,
              type: 'line',
              source: SourceNDVIName + id,
              paint: {
                'line-color': '#fff',
                'line-width': 2,
              },
            });
          }

          // loading NDVI values in parallel for all fields
          const ndviPromise = GetNDVIArrayFromImage(imageUrl)
            .then((avgNDVI) => ({
              id,
              sensorLng,
              sensorLat,
              date: queryDate,
              avgNDVI: avgNDVI,
            }))
            .catch((error) => {
              console.error(`Error loading NDVI for ${id}:`, error);
              return {
                id,
                sensorLng,
                sensorLat,
                date: queryDate,
                avgNDVI: null,
              };
            });

          ndviPromises.push(ndviPromise);
        } else {
          list.push({
            id,
            sensorLng,
            sensorLat,
            date: queryDate,
            avgNDVI: listSourceNDVI.find((item) => item.id === id).avgNDVI,
          });
        }
      }
    });

    return Promise.all(ndviPromises)
      .then((ndviResults) => {
        const finalList = [...list, ...ndviResults];
        listSourceNDVIRef.current = finalList;
        setListSourceNDVI(finalList);
        return finalList;
      })
      .catch((error) => {
        console.error('Error processing NDVI:', error);
        listSourceNDVIRef.current = list;
        setListSourceNDVI(list);
        return list;
      });
  };

  // Add markers for NDVI dates
  const createMarkersDatesNDVI = (dates) => {
    const markers = [];

    // remove markers with dates
    if (markersDate.length) {
      markersDate.forEach((marker) => marker.remove());
      setMarkersDate([]);
    }

    const actualListSourceNDVI = listSourceNDVIRef.current || [];

    // add markers with dates
    for (const field of filteredFields) {
      if (field.locations.length && field.id !== 'unbound') {
        const location = field.locations[0];
        const sensorHub = dates.filter((obj) => obj.locationId === location.id);

        if (sensorHub.length) {
          try {
            // get avg NDVI for this field
            let avgNDVI = '';
            if (actualListSourceNDVI && actualListSourceNDVI.length > 0) {
              const dataItem = actualListSourceNDVI.find((item) => item.id === sensorHub[0].locationId);
              if (dataItem && dataItem.avgNDVI) {
                avgNDVI = dataItem.avgNDVI;
              }
            }

            let targetDate = new Date(selectedDate);
            const queryDate = getClosestDate(sensorHub[0].dates, targetDate) || selectedDate;

            const ref = React.createRef();
            ref.current = document.createElement('div');
            createRoot(ref.current).render(<MapMarkerDate value={queryDate} avg={avgNDVI} />);
            // bbox = [minLng, minLat, maxLng, maxLat], setLngLat(Lng, Lat)
            const maxLngCentered = (sensorHub[0].bbox[2] - sensorHub[0].bbox[0]) / 2 + sensorHub[0].bbox[0];

            if (map.current) {
              const marker = new mapboxgl.Marker(ref.current)
                .setLngLat([maxLngCentered, sensorHub[0].bbox[1]])
                .addTo(map.current);
              markers.push(marker);
            }
          } catch (err) {
            console.error(`Error processing marker for location ${location.id}:`, err);
          }
        }
      }
    }
    setMarkersDate(markers);
  };

  // Mobile view
  const changeMobileView = () => {
    setActiveAsideMobile(!activeAsideMobile);
  };

  // On click navigator button in sensor list
  const onOpenNavigator = ({ location }) => {
    navigate({
      pathname: `/${PathnamesMap.navigator}`,
      search: `?lat=${location.lat}&lng=${location.lng}&name=${location.sensorName}`,
    });
  };

  // Set default date
  useEffect(() => {
    if (!selectedDate || selectedDate === 'undefined' || selectedDate === 'null') {
      const today = dayjs().format(DateFormat);
      dispatch(selectDate(today));
      searchParams.set('date', today);
    }
  }, [selectedDate]);

  // Get sensors dates query error
  useEffect(() => {
    if (isGetSensorsDatesError && getSensorsDatesError) {
      const errors = getSensorsDatesError.data.errors;
      if (errors.length) {
        setErrorMessage(errors[0].detail);
      }
    }
  }, [isGetSensorsDatesError, getSensorsDatesError]);

  // Init mapbox (only once)
  useEffect(() => {
    if (map.current || dates.length === 0 || !selectedDate || selectedDate === 'undefined' || selectedDate === 'null') return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapBoxStyles,
      center: mapLng && mapLat ? [mapLng, mapLat] : [lng, lat],
      zoom: zoom,
      hash: true,
    });

    map.current.addControl(new mapboxgl.NavigationControl());

    if (searchParams.get('tab')) {
      dispatch(updateSorting({ dir, parameter, crop: searchParams.get('tab') }));
      setFilterCrop(searchParams.get('tab'));
    } else if (crop !== 'all') {
      searchParams.set('tab', crop);
    }
    if (!searchParams.get('sort')) {
      searchParams.set('sort', dir + parameter);
    }

    map.current.on('style.load', () => {
      setStyleMapLoaded(true);
    });

    map.current.on('moveend', () => {
      const center = map.current.getCenter();
      setMapLat(center.lat);
      setMapLng(center.lng);
    });

    const handleZoom = debounce(() => {
      const currentZoom = map.current.getZoom();
      setZoom(currentZoom);
    }, 100);

    map.current.on('zoom', handleZoom);

    searchParams.set('date', selectedDate);
    setSearchParams(searchParams, { replace: true });
  }, [dates.length, selectedDate]);

  // First render data / get crops list for filter
  useEffect(() => {
    if (!isFieldsSuccess || !isSensorsSuccess || isGetSensorsLoading || !selectedDate) return;

    // Crops filter
    const cropsValues = [];
    fields.map((obj) => {
      if (obj.seasons) {
        obj.seasons.map((objSeason) => {
          const locations = sensors.filter((sensor) => sensor.location.field_season_id === objSeason?.id);
          if (
            locations &&
            locations.length &&
            (cropsValues.length === 0 || !cropsValues.find((c) => c.id === objSeason.crop_id))
          ) {
            cropsValues.push({
              id: objSeason.crop_id,
              color: crops.filter((item) => item.id === objSeason.crop_id)[0].color,
            });
          }
        });
      }
    });

    setCurrentCrops(cropsValues);
    if (!cropsValues.find((item) => crop === item.id)) {
      setFilterCrop('all');
      dispatch(updateSorting({ dir, parameter, crop: 'all' }));
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [isFieldsSuccess, isSensorsSuccess, isGetSensorsLoading, selectedDate]);

  // Get filtered fields list
  useEffect(() => {
    if (
      !isFieldsSuccess ||
      !isBoundariesSuccess ||
      !isSensorsSuccess ||
      !map.current ||
      isGetSensorsLoading ||
      isGetSensorsDatesLoading ||
      !isGetSensorsDatesSuccess ||
      !styleMapLoaded
    ) {
      return;
    }

    //remove markers
    if (markers.length) {
      markers.forEach((marker) => marker.remove());
    }
    setMarkers([]);

    //remove markers fields
    if (markersFields.length) {
      markersFields.forEach((marker) => marker.remove());
    }
    setMarkersFields([]);

    //remove polygons
    if (polygons.length) {
      polygons.forEach((source) => {
        removePolygonSources(source.id, source.layerFillId, source.layerOutlineId, map.current);
      });
      setPolygons([]);
    }

    const list = [];
    const mapMarkers = [];
    const mapMarkersFields = [];
    const mapPolygons = [];
    let centeredMarkersValue = centeredMarkers;

    fields.map((obj) => {
      let season = [];
      if (obj.seasons && obj.seasons.length) {
        const filteredSeasons = crop === 'all' ? obj.seasons : obj.seasons.filter((item) => item.crop_id === crop);
        filteredSeasons.map((objSeason) => {
          // if (isDateInPeriod(objSeason.sowing_date, objSeason.harvest_date, selectedDate)) {
          const seasonBoundary = boundaries.filter((boundary) => boundary.id === objSeason.boundary_id)[0];
          if (seasonBoundary) {
            season.push({
              id: objSeason.id,
              crop: objSeason.crop_id,
              color: crops.filter((item) => item.id === objSeason.crop_id)[0].color,
              coordinates: seasonBoundary.geom.coordinates,
              sowingDate: objSeason.sowing_date,
              harvestDate: objSeason.harvest_date,
              boundaryId: objSeason.boundary_id,
            });
          }
          // }
        });

        const listLocations = [];
        const locations = season
          ? sensors.filter((sensor) => season.find((item) => item.id === sensor.location.field_season_id))
          : [];
        if (locations.length) {
          locations.map((objLocation) => {
            listLocations.push(GetLocationValues(objLocation));
          });
        }

        if (season && listLocations.length) {
          list.push({
            id: obj.id,
            name: obj.name,
            season: season[0],
            locations: listLocations,
          });
        }
      }
    });

    // Unbound sensors
    const unboundList = [];
    sensors.map((obj) => {
      if (!obj.location.field_season_id) {
        unboundList.push(GetLocationValues(obj));
      }
    });
    setUnboundSensors(unboundList);
    list.push({
      id: 'unbound',
      name: t('sensor.unboundSensors'),
      season: null,
      locations: unboundList,
    });

    // Markers and Fields
    list.map((obj, index) => {
      if (obj.season) {
        const season = obj.season;
        map.current.addSource(`polygon-${index}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: season.coordinates[0],
            },
          },
        });

        map.current.addLayer({
          id: `polygon-fill-${index}`,
          type: 'fill',
          source: `polygon-${index}`,
          layout: {},
          paint: {
            'fill-color': DefaultBgPolygon,
            'fill-opacity': 0.4,
          },
        });

        map.current.addLayer({
          id: `polygon-outline-${index}`,
          type: 'line',
          source: `polygon-${index}`,
          layout: {},
          paint: {
            'line-color': ActiveBorderPolygon,
            'line-width': 2,
          },
        });
        mapPolygons.push({
          id: `polygon-${index}`,
          layerFillId: `polygon-fill-${index}`,
          layerOutlineId: `polygon-outline-${index}`,
        });

        // Marker with field's name
        const centroid = getCentroidCoordinates(obj.season.coordinates[0][0]);
        const ref = React.createRef();
        ref.current = document.createElement('div');
        createRoot(ref.current).render(
          <MapMarkerField text={obj.season.crop} date={obj.season.sowingDate} fieldName={obj.name} t={t} />,
        );
        const marker = new mapboxgl.Marker(ref.current).setLngLat(centroid).addTo(map.current);
        mapMarkersFields.push(marker);
      }
      if (obj.locations.length) {
        obj.locations.map((location) => {
          const ref = React.createRef();
          ref.current = document.createElement('div');

          const markerValue = GetMarkerValue({ param: filterParam, location });

          createRoot(ref.current).render(
            <MapMarker
              crop={obj.season && obj.season.crop ? obj.season.crop : ''}
              color={
                filterParam === 'bbchAI' && location[filterParam]
                  ? 'var(--cropler-ai-gradient-vertical)'
                  : obj.season
                    ? obj.season.color
                    : '#ccc'
              }
              value={markerValue}
              imgDay={location.imgDay ? location.imgDay.url : ''}
              imgNight={location.imgNight ? location.imgNight.url : ''}
              imgType={photosType}
              name={location.name}
              description={filterParam === 'bbchAI' && location.bbchAI ? location.bbchAI.description : null}
              onClickMarker={() =>
                clickMarker({
                  id: location.id,
                  image: photosType === 'day' || photosType === 'ndvi' ? location.imgDay.url : location.imgNight.url,
                })
              }
              onClickImage={() =>
                clickImage({
                  image: photosType === 'day' || photosType === 'ndvi' ? location.imgDay.url : location.imgNight.url,
                })
              }
              t={t}
            />,
          );

          const marker = new mapboxgl.Marker(ref.current).setLngLat([location.lng, location.lat]).addTo(map.current);
          marker._locationId = location.id;
          mapMarkers.push(marker);

          // first fly to marker
          if (
            !selectedField &&
            !centeredMarkersValue &&
            (!mapLat || !mapLng) &&
            (location.id === selectedSensor || !selectedSensor)
          ) {
            flyToLocation({
              longitude: location.lng,
              latitude: location.lat,
              seasonId: location.field_season_id,
              locationId: location.id,
              markersList: mapMarkers,
            });
            centeredMarkersValue = true;
            setCenteredMarkers(true);
          } else if (
            mapLat &&
            mapLng &&
            roundOffNumber(mapLat) === roundOffNumber(location.lat) &&
            roundOffNumber(mapLng) === roundOffNumber(location.lng)
          ) {
            // Select a location after reload page
            setCenteredLocation(location.id);
          }
        });

        if (obj.season && selectedField === obj.season.id && !centeredMarkersValue && (!mapLat || !mapLng)) {
          flyToField({
            season: obj.season,
            locationId: obj.locations[0].id,
          });
          centeredMarkersValue = true;
          setCenteredMarkers(true);
        }
      }
    });

    setFilteredFields(list);
    setMarkers(mapMarkers);
    setMarkersFields(mapMarkersFields);
    setPolygons(mapPolygons);
  }, [
    isFieldsSuccess,
    isBoundariesSuccess,
    isSensorsSuccess,
    isGetSensorsDatesSuccess,
    isGetSensorsLoading,
    isGetSensorsDatesLoading,
    fields,
    sensors,
    boundaries,
    filterParam,
    crop,
    photosType,
    styleMapLoaded,
  ]);

  // Get NDVI
  useEffect(() => {
    if (!isFieldsSuccess || !selectedDate) {
      return;
    }
    if (!activeNDVI) {
      if (markersDate.length) {
        markersDate.forEach((marker) => marker.remove());
        setMarkersDate([]);
      }
    } else {
      getHubImagesDates();
    }

    // show/hide NDVI layers
    if (listSourceNDVI.length) {
      const visibility = !activeNDVI ? 'none' : 'visible';
      listSourceNDVI.map((item) => {
        map.current.setLayoutProperty(LayerNDVIName + item.id, 'visibility', visibility);
        map.current.setLayoutProperty(WmsLayerImageName + item.id, 'visibility', visibility);
      });
    }
  }, [activeNDVI, filteredFields]);

  if (isGetSensorsDatesLoading && !dates.length) {
    return <Loading />;
  }

  return (
    <>
      {dates.length > 0 ? (
        <div className={`workspace ${styles.mapContainer} ${zoom > 14.5 ? styles.mapContainerZoom : ''}`}>
          {/* Sidebar */}
          {(widthViewport > BreakpointMobile || activeAsideMobile) && (
            <Sidebar key={filterCrop.toString()} isPanelOpened={true} isStatic={true} openedTopDefault={59 + getSafeAreaInsetTop()}>
              {widthViewport > BreakpointMobile && (
                <SidebarFilter>
                  <CropsSelect
                    cropsList={currentCrops}
                    currentCrop={filterCrop}
                    isFilter={true}
                    changeCrop={onChangeFilterCrop}
                    t={t}
                  />
                </SidebarFilter>
              )}
              {/*<div className={stylesSidebar.sidebarFilter}>*/}
              {/*  <div className={stylesSidebar.sidebarFilterRow}>*/}
              {/*    {widthViewport < BreakpointMobileUp && (*/}
              {/*      <Button*/}
              {/*        type="secondary"*/}
              {/*        icon={activeAsideMobile ? <MapIcon /> : <ListIcon />}*/}
              {/*        onClick={() => setActiveAsideMobile(!activeAsideMobile)}*/}
              {/*      ></Button>*/}
              {/*    )}*/}
              {/*    <CropsSelect*/}
              {/*      cropsList={currentCrops}*/}
              {/*      currentCrop={filterCrop}*/}
              {/*      isFilter={true}*/}
              {/*      changeCrop={onChangeFilterCrop}*/}
              {/*    />*/}
              {/*    <SortingFormButton onClickButton={() => setActiveSortingPanel(true)} />*/}
              {/*  </div>*/}
              {/*  {widthViewport > BreakpointMobile && <SearchBar />}*/}
              {/*</div>*/}
              {/*{activeSortingPanel && (*/}
              {/*  <div className={stylesSidebar.sidebarSorting}>*/}
              {/*    <Panel title="Sorting" isRelative={true} setActivePanel={() => setActiveSortingPanel(false)}>*/}
              {/*      <SortingForm parameters={SortingParameters} />*/}
              {/*    </Panel>*/}
              {/*  </div>*/}
              {/*)}*/}
              {(widthViewport > BreakpointMobile || activeAsideMobile) && (
                <SidebarList>
                  {/*Sensors List*/}
                  {filteredFields.length > 0 &&
                    filteredFields.map((item) => {
                      return item.id !== 'unbound' && item.locations.length ? (
                        <SidebarField
                          key={item.id}
                          title={item.name}
                          crop={item.season.crop}
                          startDate={item.season.sowingDate}
                          endDate={item.season.harvestDate}
                          isActive={centeredField === item.season.boundaryId}
                          onClickItem={() => flyToField({ locationId: item.locations[0].id, season: item.season })}
                          t={t}
                        >
                          {item.locations.length > 0 &&
                            item.locations.map((location, index) => {
                              return (
                                <SidebarItemButton
                                  key={location.id}
                                  title={location.name ? location.name : t('empty.noName')}
                                  isActive={centeredLocation === location.id}
                                  showNavigatorButton={true}
                                  isAiEnabled={location.isAiEnabled}
                                  onClickButton={() =>
                                    flyToLocation({
                                      longitude: item.locations[index].lng,
                                      latitude: item.locations[index].lat,
                                      locationId: location.id,
                                      seasonId: location.field_season_id,
                                    })
                                  }
                                  onClickNavigator={() => onOpenNavigator({ location })}
                                  t={t}
                                />
                              );
                            })}
                        </SidebarField>
                      ) : (
                        ''
                      );
                    })}

                  {/*Unbound Sensors*/}
                  {unboundSensors.length > 0 && (
                    <SidebarListExpanded t={t}>
                      {unboundSensors.map((item) => {
                        return (
                          <SidebarItemButton
                            key={item.id}
                            title={item.name ? item.name : t('empty.noName')}
                            isActive={centeredLocation === item.id}
                            isAiEnabled={item.isAiEnabled}
                            showAddSeasonButton={true}
                            showNavigatorButton={true}
                            onClickButton={() =>
                              flyToLocation({
                                longitude: item.lng,
                                latitude: item.lat,
                                locationId: item.id,
                                seasonId: item.field_season_id,
                              })
                            }
                            onClickAddSeason={() => onAddField()}
                            t={t}
                          />
                        );
                      })}
                    </SidebarListExpanded>
                  )}
                </SidebarList>
              )}
            </Sidebar>
          )}

          {/* Map */}
          <div ref={mapContainer} className={`workspace-body`} />

          {/* Filters on the Map */}
          <div className={styles.filter}>
            {widthViewport < BreakpointMobileUp && (
              <MapMobileSwitchView onClickButton={() => changeMobileView()} isListActive={activeAsideMobile} />
            )}
            <FilterDate
              allDates={dates}
              selectedDate={selectedDate}
              selectDate={changeDate}
              isDarkTheme={widthViewport > BreakpointMobileUp}
            />
            {/*Metrics mobile*/}
            {widthViewport < BreakpointMobileUp && (
              <MapFilterMetricsParam
                t={t}
                param={filterParam}
                clickFilterParameter={() => setActiveParametersMobile(true)}
                isDarkTheme={widthViewport > BreakpointMobileUp}
                widthViewport={widthViewport}
              />
            )}
            {/*Metrics*/}
            {(widthViewport > BreakpointMobile || activeParametersMobile) && (
              <MapFilterMetrics
                filterParam={filterParam}
                onChangeFilterParam={onChangeFilterParam}
                onClickClose={() => setActiveParametersMobile(false)}
                t={t}
              />
            )}
          </div>

          {/* Toggle NDVI */}
          <NDVIToggleButton
            isActive={activeNDVI}
            widthViewport={widthViewport}
            onClickButton={() => toggleNDVITTiles({ status: !activeNDVI })}
            t={t}
          />

          {/* NDVI scale */}
          {activeNDVI && widthViewport > BreakpointMobileUp && <NdviScale />}

          {/* Switch day/night RGB photo type */}
          <div className={styles.filterButtonTypeSwitch}>
            <PhotosTypeSwitchDark />
          </div>
        </div>
      ) : (
        <EmptyView message={errorMessage} t={t} />
      )}

      <Modal
        centered
        footer={null}
        onCancel={() => setModalOpen(false)}
        open={modalOpen}
        width={1200}
        className="modalNoPadding"
      >
        <div className={styles.imageBoxModal}>
          <img src={imageFull} alt="" />
          {widthViewport < BreakpointMobileUp && (
            <div className={styles.mapModalNav}>
              <Button onClick={() => goToTimeline({ id: activeMarkerId, date: selectedDate, navigate })}>
                {t('map.goToCropTimeline')}
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default Map;
